/**
 * Resolve chapter requests (numbers, ranges, titles) for desktop assistant chat + get_chapters tool.
 */

import { chapterContentToPlainText } from "./chapterContent";
import { normalizeDialogueParagraphBreaks } from "./chapterVariationDialogue";
import { variationRewriteValidationError } from "./chapterVariationValidate";

export type ChatBookChapter = {
  id: string;
  title: string;
  order: number;
  content: string;
};

export type ChatBookSnapshot = {
  title: string;
  chapters: ChatBookChapter[];
  researchAssets?: Array<{
    id: string;
    kind: string;
    title: string;
    extractedText?: string;
    notes?: string;
    tags?: string[];
    sourceUrl?: string;
  }>;
};

export function buildChapterCatalog(chapters: ChatBookChapter[]): string {
  const sorted = [...chapters].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  return sorted.map((c, i) => `${c.order ?? i + 1}. "${c.title}"`).join("\n");
}

function sortedChapters(chapters: ChatBookChapter[]): ChatBookChapter[] {
  return [...chapters].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** Parse user message or tool `chapters_spec` into chapter rows from the open book. */
export function resolveChaptersFromRequest(spec: string, chapters: ChatBookChapter[]): ChatBookChapter[] {
  const mentioned: ChatBookChapter[] = [];
  const ordered = sortedChapters(chapters);
  const message = spec.trim();
  if (!message) return [];

  const lowerMessage = message.toLowerCase();

  const addChapterByNumber = (num: number) => {
    const byOrder = ordered.find((c) => c.order === num);
    if (byOrder && !mentioned.some((mc) => mc.id === byOrder.id)) {
      mentioned.push(byOrder);
      return;
    }
    if (num < 1 || num > ordered.length) return;
    const chapter = ordered[num - 1];
    if (chapter && !mentioned.some((mc) => mc.id === chapter.id)) {
      mentioned.push(chapter);
    }
  };

  const rangeRegex = /\b(?:chapters?\s+)?(\d+)\s*(?:through|thru|to|-|–)\s*(\d+)\b/gi;
  let rangeMatch: RegExpExecArray | null;
  while ((rangeMatch = rangeRegex.exec(message)) !== null) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
      addChapterByNumber(i);
    }
  }

  const andRegex = /\b(?:chapters?)\s*(\d+)\s*(?:&|and)\s*(\d+)\b/gi;
  let andMatch: RegExpExecArray | null;
  while ((andMatch = andRegex.exec(message)) !== null) {
    addChapterByNumber(parseInt(andMatch[1], 10));
    addChapterByNumber(parseInt(andMatch[2], 10));
  }

  const andFollowupRegex = /\band\s+(\d+)\b(?!\s*(?:through|thru|to|-|–))/gi;
  let andFollowup: RegExpExecArray | null;
  while ((andFollowup = andFollowupRegex.exec(message)) !== null) {
    addChapterByNumber(parseInt(andFollowup[1], 10));
  }

  const listRegex = /\b(?:chapters?)\s*([\d,\s&and]+)/gi;
  let listMatch: RegExpExecArray | null;
  while ((listMatch = listRegex.exec(message)) !== null) {
    const numbers = listMatch[1].match(/\d+/g);
    if (numbers) {
      for (const numStr of numbers) {
        addChapterByNumber(parseInt(numStr, 10));
      }
    }
  }

  const chapterNumRegex = /\b(?:chapter|ch\.?)\s*(\d+)\b/gi;
  let numMatch: RegExpExecArray | null;
  while ((numMatch = chapterNumRegex.exec(message)) !== null) {
    addChapterByNumber(parseInt(numMatch[1], 10));
  }

  const readRegex = /\b(?:read|load|get|show|include|analyze|review)\b[^.]*?(\d+(?:\s*(?:,|&|and)\s*\d+)*)/gi;
  let readMatch: RegExpExecArray | null;
  while ((readMatch = readRegex.exec(message)) !== null) {
    const numbers = readMatch[1].match(/\d+/g);
    if (numbers) {
      for (const numStr of numbers) {
        addChapterByNumber(parseInt(numStr, 10));
      }
    }
  }

  for (const chapter of ordered) {
    if (chapter.title.trim() && lowerMessage.includes(chapter.title.toLowerCase())) {
      if (!mentioned.some((mc) => mc.id === chapter.id)) {
        mentioned.push(chapter);
      }
    }
  }

  mentioned.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  return mentioned;
}

export function formatChaptersForAssistantContext(
  chapters: ChatBookChapter[],
  bookChapters: ChatBookChapter[]
): string {
  const ordered = sortedChapters(bookChapters);
  const parts: string[] = [];
  for (const chapter of chapters) {
    const index = ordered.findIndex((c) => c.id === chapter.id);
    const num = chapter.order ?? (index >= 0 ? index + 1 : 1);
    const text = chapterContentToPlainText(chapter.content ?? "").trim();
    parts.push(
      `## Chapter ${num}: "${chapter.title}"\nWord count: ${text.split(/\s+/).filter(Boolean).length}\n\n${text}\n\n--- END OF CHAPTER ${num} ---`
    );
  }
  return parts.join("\n\n");
}

export function executeGetChaptersTool(chaptersSpec: string, snapshot: ChatBookSnapshot): string {
  const spec = chaptersSpec.trim();
  if (!spec) {
    return `Error: chapters_spec is empty. Provide chapter numbers, ranges, or titles. Catalog:\n${buildChapterCatalog(snapshot.chapters)}`;
  }
  const resolved = resolveChaptersFromRequest(spec, snapshot.chapters);
  if (resolved.length === 0) {
    return `No chapters matched "${spec}". Available chapters:\n${buildChapterCatalog(snapshot.chapters)}`;
  }
  const nums = resolved.map((c) => {
    const idx = sortedChapters(snapshot.chapters).findIndex((ch) => ch.id === c.id);
    return idx >= 0 ? idx + 1 : c.order;
  });
  return `Loaded ${resolved.length} chapter(s): ${nums.join(", ")}\n\n${formatChaptersForAssistantContext(resolved, snapshot.chapters)}`;
}

export const GET_CHAPTERS_TOOL_OPENAI = {
  type: "function" as const,
  function: {
    name: "get_chapters",
    description:
      "Load full manuscript text for chapters the user asked about. Pass a chapters_spec string with chapter numbers, ranges, titles, or natural language (e.g. '17-21', 'chapters 3 and 5', 'Whispers Under the Floorboards'). Call this before answering when the user references chapters other than those already in context.",
    parameters: {
      type: "object",
      properties: {
        chapters_spec: {
          type: "string",
          description:
            "Which chapters to load — numbers, ranges, titles, or a phrase from the user request (e.g. '17 through 21', 'chapter 1 and chapter 2').",
        },
      },
      required: ["chapters_spec"],
    },
  },
};

export const GET_CHAPTERS_TOOL_ANTHROPIC = {
  name: "get_chapters",
  description:
    "Load full manuscript text for chapters the user asked about. Pass chapters_spec with numbers, ranges, titles, or natural language.",
  input_schema: {
    type: "object",
    properties: {
      chapters_spec: {
        type: "string",
        description: "Chapter numbers, ranges, or titles to load (e.g. '17-21', 'chapters 3 and 5').",
      },
    },
    required: ["chapters_spec"],
  },
};

export type ChapterReorderPosition = "before" | "after";

export type ChatBookMutation =
  | {
      type: "reorder_chapters";
      chapterIds: string[];
    }
  | {
      type: "create_chapter_variation";
      chapterId: string;
      content: string;
      prompt: string;
      note?: string;
    };

export type MoveChapterResult = {
  chapters: ChatBookChapter[];
  error?: string;
};

/** Reorder by chapter id — used by UI and after resolving assistant specs. */
export function reorderChaptersById(
  chapters: ChatBookChapter[],
  sourceId: string,
  targetId: string,
  position: ChapterReorderPosition
): MoveChapterResult {
  if (sourceId === targetId) {
    return { chapters, error: "Source and target chapter must differ." };
  }
  const ordered = sortedChapters(chapters);
  const source = ordered.find((c) => c.id === sourceId);
  const target = ordered.find((c) => c.id === targetId);
  if (!source) return { chapters, error: "Source chapter was not found." };
  if (!target) return { chapters, error: "Target chapter was not found." };

  const withoutSource = ordered.filter((c) => c.id !== sourceId);
  const targetIndex = withoutSource.findIndex((c) => c.id === targetId);
  if (targetIndex < 0) return { chapters, error: "Target chapter was not found." };

  const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
  withoutSource.splice(insertIndex, 0, source);
  return {
    chapters: withoutSource.map((chapter, index) => ({ ...chapter, order: index + 1 })),
  };
}

/** Move one chapter before or after another using numbers, ranges, or titles in specs. */
export function moveChapterRelativeTo(
  chapters: ChatBookChapter[],
  sourceSpec: string,
  targetSpec: string,
  position: ChapterReorderPosition
): MoveChapterResult {
  const sources = resolveChaptersFromRequest(sourceSpec, chapters);
  if (sources.length === 0) {
    return {
      chapters,
      error: `No source chapter matched "${sourceSpec}".`,
    };
  }
  if (sources.length > 1) {
    return {
      chapters,
      error: `Source spec "${sourceSpec}" matched multiple chapters. Specify one chapter number or title.`,
    };
  }

  const targets = resolveChaptersFromRequest(targetSpec, chapters);
  if (targets.length === 0) {
    return {
      chapters,
      error: `No target chapter matched "${targetSpec}".`,
    };
  }
  if (targets.length > 1) {
    return {
      chapters,
      error: `Target spec "${targetSpec}" matched multiple chapters. Specify one chapter number or title.`,
    };
  }

  return reorderChaptersById(chapters, sources[0].id, targets[0].id, position);
}

export type MoveChapterToolResult = {
  message: string;
  chapters?: ChatBookChapter[];
  mutation?: ChatBookMutation;
};

export function executeMoveChapterTool(
  sourceSpec: string,
  targetSpec: string,
  position: ChapterReorderPosition,
  snapshot: ChatBookSnapshot
): MoveChapterToolResult {
  const result = moveChapterRelativeTo(snapshot.chapters, sourceSpec, targetSpec, position);
  if (result.error) {
    return {
      message: `Error: ${result.error}\n\nAvailable chapters:\n${buildChapterCatalog(snapshot.chapters)}`,
    };
  }

  const sources = resolveChaptersFromRequest(sourceSpec, snapshot.chapters);
  const targets = resolveChaptersFromRequest(targetSpec, snapshot.chapters);
  const source = sources[0];
  const target = targets[0];
  const newOrder = result.chapters.findIndex((c) => c.id === source.id) + 1;

  return {
    message: `Moved "${source.title}" ${position} "${target.title}" (now chapter ${newOrder}).`,
    chapters: result.chapters,
    mutation: {
      type: "reorder_chapters",
      chapterIds: result.chapters.map((chapter) => chapter.id),
    },
  };
}

export const MOVE_CHAPTER_TOOL_OPENAI = {
  type: "function" as const,
  function: {
    name: "move_chapter",
    description:
      "Move a chapter before or after another chapter in the manuscript order. Use when the user asks to reorder, move, or reposition chapters.",
    parameters: {
      type: "object",
      properties: {
        source_spec: {
          type: "string",
          description: "Chapter to move — number, title, or phrase from the catalog (e.g. '5', 'Whispers Under the Floorboards').",
        },
        target_spec: {
          type: "string",
          description: "Reference chapter — number or title to move relative to.",
        },
        position: {
          type: "string",
          enum: ["before", "after"],
          description: "Place the source chapter immediately before or after the target chapter.",
        },
      },
      required: ["source_spec", "target_spec", "position"],
    },
  },
};

export const MOVE_CHAPTER_TOOL_ANTHROPIC = {
  name: "move_chapter",
  description:
    "Move a chapter before or after another chapter in the manuscript order. Use when the user asks to reorder chapters.",
  input_schema: {
    type: "object",
    properties: {
      source_spec: {
        type: "string",
        description: "Chapter to move — number or title from the catalog.",
      },
      target_spec: {
        type: "string",
        description: "Reference chapter — number or title.",
      },
      position: {
        type: "string",
        enum: ["before", "after"],
        description: "before or after the target chapter.",
      },
    },
    required: ["source_spec", "target_spec", "position"],
  },
};

export const CHAT_TOOL_SYSTEM_HINT =
  "You can load any chapter manuscript with get_chapters. You can reorder chapters with move_chapter (source_spec, target_spec, position before|after). You can search the book's research library with search_research_assets and load one asset with get_research_asset. The chapter catalog is in context; only some chapter bodies may be loaded initially. When the user asks about chapters you cannot see, call get_chapters before answering.";

export const SEARCH_RESEARCH_ASSETS_TOOL_OPENAI = {
  type: "function" as const,
  function: {
    name: "search_research_assets",
    description: "Search the open book's research library by keyword (title, tags, notes, extracted text).",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string", description: "Search text." },
      },
      required: ["query"],
    },
  },
};

export const SEARCH_RESEARCH_ASSETS_TOOL_ANTHROPIC = {
  name: "search_research_assets",
  description: "Search the open book's research library by keyword.",
  input_schema: {
    type: "object",
    properties: { query: { type: "string" } },
    required: ["query"],
  },
};

export const GET_RESEARCH_ASSET_TOOL_OPENAI = {
  type: "function" as const,
  function: {
    name: "get_research_asset",
    description: "Load extracted text and notes for one research asset by id or title.",
    parameters: {
      type: "object",
      properties: {
        asset_spec: { type: "string", description: "Asset id or title." },
      },
      required: ["asset_spec"],
    },
  },
};

export const GET_RESEARCH_ASSET_TOOL_ANTHROPIC = {
  name: "get_research_asset",
  description: "Load one research asset by id or title.",
  input_schema: {
    type: "object",
    properties: { asset_spec: { type: "string" } },
    required: ["asset_spec"],
  },
};

export function executeSearchResearchAssets(query: string, snapshot: ChatBookSnapshot): string {
  const assets = snapshot.researchAssets ?? [];
  if (!assets.length) return "The research library is empty.";
  const q = query.trim().toLowerCase();
  const hits = !q
    ? assets.slice(0, 8)
    : assets.filter((asset) =>
        [asset.title, asset.kind, asset.notes ?? "", asset.extractedText ?? "", ...(asset.tags ?? [])]
          .join("\n")
          .toLowerCase()
          .includes(q)
      ).slice(0, 8);
  if (!hits.length) return `No research assets matched "${query}".`;
  return hits
    .map((asset) => `- ${asset.id} · ${asset.kind} · ${asset.title}`)
    .join("\n");
}

export function executeGetResearchAsset(spec: string, snapshot: ChatBookSnapshot): string {
  const assets = snapshot.researchAssets ?? [];
  const needle = spec.trim().toLowerCase();
  const asset =
    assets.find((a) => a.id.toLowerCase() === needle) ||
    assets.find((a) => a.title.toLowerCase() === needle) ||
    assets.find((a) => a.title.toLowerCase().includes(needle));
  if (!asset) return `No research asset matched "${spec}".`;
  const notes = asset.notes?.trim() ? `\nNotes:\n${asset.notes.trim()}` : "";
  const body = asset.extractedText?.trim() || "(No extracted text.)";
  return `${asset.kind}: ${asset.title}${asset.sourceUrl ? ` (${asset.sourceUrl})` : ""}${notes}\n\n${body.slice(0, 12000)}`;
}

export type CreateChapterVariationToolResult = {
  message: string;
  mutation?: ChatBookMutation;
};

export function executeCreateChapterVariationTool(params: {
  rewrittenContent: string;
  chapterSpec: string;
  prompt: string;
  snapshot: ChatBookSnapshot;
  activeChapterId?: string;
  note?: string;
}): CreateChapterVariationToolResult {
  const rewritten = normalizeDialogueParagraphBreaks(params.rewrittenContent.trim());
  if (!rewritten) {
    return { message: "Error: rewritten_content is empty. Provide the full rewritten chapter text." };
  }

  let chapterId = params.activeChapterId?.trim() ?? "";
  let chapterTitle = "";
  const spec = params.chapterSpec.trim();
  if (spec) {
    const resolved = resolveChaptersFromRequest(spec, params.snapshot.chapters);
    if (resolved.length === 0) {
      return {
        message: `No chapter matched "${spec}". Available chapters:\n${buildChapterCatalog(params.snapshot.chapters)}`,
      };
    }
    if (resolved.length > 1) {
      return {
        message: `Chapter spec "${spec}" matched multiple chapters. Specify one chapter number or title.`,
      };
    }
    chapterId = resolved[0].id;
    chapterTitle = resolved[0].title;
  } else if (chapterId) {
    const chapter = params.snapshot.chapters.find((entry) => entry.id === chapterId);
    if (!chapter) {
      return {
        message: `Active chapter was not found. Available chapters:\n${buildChapterCatalog(params.snapshot.chapters)}`,
      };
    }
    chapterTitle = chapter.title;
  } else {
    return {
      message: `Error: specify chapter_spec or ensure an active chapter is selected. Available chapters:\n${buildChapterCatalog(params.snapshot.chapters)}`,
    };
  }

  const baseChapter = params.snapshot.chapters.find((entry) => entry.id === chapterId);
  const basePlain = chapterContentToPlainText(baseChapter?.content ?? "");
  const validationError = variationRewriteValidationError(rewritten, basePlain);
  if (validationError) {
    return {
      message: `Error: ${validationError} Call create_chapter_variation again with the full rewritten chapter prose in rewritten_content.`,
    };
  }

  return {
    message: `Created a variation for "${chapterTitle}". The compare view is open — summarize what changed in a short reply (do not repeat the full rewrite).`,
    mutation: {
      type: "create_chapter_variation",
      chapterId,
      content: rewritten,
      prompt: params.prompt.trim() || "Generate a variation of this chapter",
      note: params.note?.trim() || undefined,
    },
  };
}

export const CREATE_CHAPTER_VARIATION_TOOL_OPENAI = {
  type: "function" as const,
  function: {
    name: "create_chapter_variation",
    description:
      "Store a full rewritten chapter as a variation for side-by-side compare. Call ONLY after clarifying questions are answered and the author confirms they are ready. Pass the complete rewritten chapter prose in rewritten_content — never plans or acknowledgements.",
    parameters: {
      type: "object",
      properties: {
        rewritten_content: {
          type: "string",
          description: "The full rewritten chapter text, paragraph breaks separated by blank lines.",
        },
        chapter_spec: {
          type: "string",
          description:
            "Optional chapter number, title, or phrase. Defaults to the active chapter when omitted.",
        },
        note: {
          type: "string",
          description: "Optional short note about what changed (shown in the compare view).",
        },
      },
      required: ["rewritten_content"],
    },
  },
};

export const CREATE_CHAPTER_VARIATION_TOOL_ANTHROPIC = {
  name: "create_chapter_variation",
  description:
    "Store a full rewritten chapter as a variation for side-by-side compare. Call ONLY after the author confirms they are ready. Pass complete rewritten chapter prose in rewritten_content.",
  input_schema: {
    type: "object",
    properties: {
      rewritten_content: {
        type: "string",
        description: "The full rewritten chapter text.",
      },
      chapter_spec: {
        type: "string",
        description: "Optional chapter number or title. Defaults to the active chapter.",
      },
      note: {
        type: "string",
        description: "Optional short note about what changed.",
      },
    },
    required: ["rewritten_content"],
  },
};
